/**
 * Framework-agnostic Zustand store factory for the founding-agent voice call.
 * No React imports, no @livekit/components-react types.
 *
 * Holds connection state, agent display state, audio track ref, mic volume,
 * finalized and streaming transcripts, chat prompts, and pipeline metrics.
 */
import { createStore } from "zustand/vanilla";
import { DEFAULT_FOUNDING_AGENT_SERVICE_URL, createSessionViaEndpoint } from "./session.js";
import { sendVisitorBeacon } from "./beacon.js";
// --------------- Neutral types ---------------
export const AGENT_DISPLAY_STATES = [
    "initializing",
    "listening",
    "thinking",
    "speaking",
    "idle",
];
export function isAgentDisplayState(v) {
    return typeof v === "string" && AGENT_DISPLAY_STATES.includes(v);
}
// --------------- Helpers ---------------
const initialPipelineMetrics = () => ({
    eouDelay: [],
    eouTranscriptionDelay: [],
    sttDuration: [],
    retrievalLatency: [],
    llmTtft: [],
    ttsTtfb: [],
    llmDuration: [],
    llmTokensPerSecond: [],
    llmPromptTokens: [],
    llmCompletionTokens: [],
    ttsDuration: [],
    ttsAudioDuration: [],
    ttsCharactersCount: [],
    sttAudioDuration: [],
    vadIdleTime: [],
    vadInferenceDuration: [],
    vadInferenceCount: [],
});
const initialBookingSlice = () => ({
    bookingOpen: false,
    bookingStep: 1,
    slots: null,
    fields: null,
    selectedStart: null,
    selectedStartLabel: null,
    submitting: false,
    bookingResult: null,
    bookingError: null,
});
// --------------- Factory ---------------
export function createVoiceAgentStore(config = {}) {
    // Bumped by each startConversation() and by reset(), so a session response
    // that resolves after a reset can't write stale credentials into the store.
    let sessionRequestGen = 0;
    return createStore((set, get) => ({
        connectionState: "idle",
        agentDisplayState: "idle",
        lastAgentStateEvent: null,
        token: null,
        wsUrl: null,
        remoteAudioTrack: null,
        agentVoiceState: null,
        userMicVolume: 0,
        agentAudioLevel: 0,
        isMicMuted: false,
        micError: null,
        micRetryNonce: 0,
        micRetryInFlight: false,
        latestPageContextPath: null,
        handoffStatus: "idle",
        handoffId: null,
        handoffOperatorName: null,
        transcript: [],
        streamingTranscripts: [],
        chatPrompts: [],
        sendChatMessage: null,
        pipelineMetrics: initialPipelineMetrics(),
        intakeConfig: null,
        intakeOpen: false,
        intakeDone: false,
        answersDelivered: 0,
        sawUserTurnSinceAnswer: false,
        wasMicMutedBeforeIntake: false,
        tokenEndpoint: config.tokenEndpoint ?? "/api/moss-token",
        publishableKey: config.publishableKey ?? "",
        serviceUrl: config.serviceUrl ?? DEFAULT_FOUNDING_AGENT_SERVICE_URL,
        bookingNonce: null,
        ...initialBookingSlice(),
        setBookingOpen: (bookingOpen) => set({ bookingOpen }),
        setBookingStep: (bookingStep) => set({ bookingStep }),
        setSlots: (slots) => set({ slots }),
        setFields: (fields) => set({ fields }),
        setSelectedStart: (selectedStart, selectedStartLabel) => set({ selectedStart, selectedStartLabel }),
        setSubmitting: (submitting) => set({ submitting }),
        setBookingResult: (bookingResult) => set({ bookingResult }),
        setBookingError: (bookingError) => set({ bookingError }),
        setBookingNonce: (bookingNonce) => set({ bookingNonce }),
        resetBooking: () => set(initialBookingSlice()),
        setConnectionState: (connectionState) => set({ connectionState }),
        setAgentDisplayState: (agentDisplayState) => set({ agentDisplayState }),
        setLastAgentStateEvent: (lastAgentStateEvent) => set({ lastAgentStateEvent }),
        setRemoteAudioTrack: (remoteAudioTrack) => set({ remoteAudioTrack }),
        setAgentVoiceState: (agentVoiceState) => set({ agentVoiceState }),
        setUserMicVolume: (userMicVolume) => set({ userMicVolume }),
        setAgentAudioLevel: (agentAudioLevel) => set({ agentAudioLevel }),
        setMicMuted: (isMicMuted) => set({ isMicMuted }),
        setMicError: (micError) => set({ micError }),
        requestMicRetry: () => {
            if (get().micRetryInFlight)
                return;
            set((prev) => ({
                micRetryNonce: prev.micRetryNonce + 1,
                micRetryInFlight: true,
            }));
        },
        setMicRetryInFlight: (micRetryInFlight) => set({ micRetryInFlight }),
        setLatestPageContextPath: (latestPageContextPath) => set({ latestPageContextPath }),
        setHandoffState: (handoffStatus, handoffId, handoffOperatorName) => set({ handoffStatus, handoffId, handoffOperatorName }),
        addPipelineMetric: (type, value) => set((prev) => ({
            pipelineMetrics: {
                ...prev.pipelineMetrics,
                [type]: [...prev.pipelineMetrics[type].slice(-99), value],
            },
        })),
        addTranscriptItem: (item) => set((prev) => ({ transcript: [...prev.transcript, item] })),
        addChatPrompt: (text) => set((prev) => {
            const now = Date.now();
            return {
                chatPrompts: [
                    ...prev.chatPrompts,
                    {
                        id: `chat-${now}`,
                        role: "user",
                        text,
                        timestamp: now,
                        isFinal: true,
                    },
                ],
                sawUserTurnSinceAnswer: true,
            };
        }),
        setSendChatMessage: (sendChatMessage) => set({ sendChatMessage }),
        setIntakeConfig: (intakeConfig) => set({ intakeConfig }),
        // No-op unless the form can actually render (enabled config + a nonce to
        // attach responses to). Without this gate an explicit call before session
        // init (or on a nonce-less session) would mute the mic with no control
        // able to call resolveIntake() and un-mute it. Also a no-op when already
        // open, so a repeat call can't clobber wasMicMutedBeforeIntake with the
        // now-muted isMicMuted (which would lose the original pre-open value),
        // and once intake is done, so the form shows at most once per session.
        openIntake: () => set((prev) => !prev.intakeConfig?.enabled || !prev.bookingNonce || prev.intakeOpen || prev.intakeDone
            ? {}
            : {
                intakeOpen: true,
                isMicMuted: true,
                wasMicMutedBeforeIntake: prev.isMicMuted,
            }),
        // Idempotent: a no-op unless the form is actually open, so a stale
        // callback (e.g. a POST that resolves after the form was already
        // dismissed/reopened) can't re-mark intake done or flip the mic state.
        resolveIntake: () => set((prev) => !prev.intakeOpen
            ? {}
            : {
                intakeOpen: false,
                intakeDone: true,
                isMicMuted: prev.wasMicMutedBeforeIntake,
            }),
        noteUserTurn: () => set({ sawUserTurnSinceAnswer: true }),
        noteAgentAnswerEdge: () => set((prev) => {
            if (!prev.sawUserTurnSinceAnswer)
                return {};
            const answersDelivered = prev.answersDelivered + 1;
            // Defensive read: the Provider normalizes intake_form before it lands
            // here, but a config set through other paths may lack a trigger. A
            // non-numeric after_answers means "never open" rather than a throw
            // inside a store subscription.
            const afterAnswers = prev.intakeConfig?.trigger?.after_answers;
            const shouldOpen = !!prev.intakeConfig?.enabled &&
                !prev.intakeDone &&
                !prev.intakeOpen &&
                !!prev.bookingNonce &&
                typeof afterAnswers === "number" &&
                answersDelivered >= afterAnswers;
            return {
                answersDelivered,
                sawUserTurnSinceAnswer: false,
                ...(shouldOpen
                    ? { intakeOpen: true, isMicMuted: true, wasMicMutedBeforeIntake: prev.isMicMuted }
                    : {}),
            };
        }),
        setStreamingTranscripts: (segments) => {
            const current = get().streamingTranscripts;
            if (current.length === segments.length && current.length > 0) {
                const c = current[current.length - 1];
                const n = segments[segments.length - 1];
                if (c.id === n.id && c.text === n.text && c.isFinal === n.isFinal)
                    return;
            }
            set({ streamingTranscripts: segments });
        },
        setTokenEndpoint: (tokenEndpoint) => set({ tokenEndpoint }),
        setPublishableKey: (publishableKey) => set({ publishableKey }),
        setServiceUrl: (serviceUrl) => set({ serviceUrl }),
        startConversation: async () => {
            const { connectionState, tokenEndpoint, publishableKey, serviceUrl } = get();
            if (connectionState === "connecting" || connectionState === "connected")
                return;
            const gen = ++sessionRequestGen;
            try {
                set({ connectionState: "connecting" });
                const sess = await createSessionViaEndpoint(tokenEndpoint);
                if (gen !== sessionRequestGen)
                    return;
                set({
                    token: sess.token,
                    wsUrl: sess.serverUrl,
                    bookingNonce: sess.bookingNonce,
                });
                void sendVisitorBeacon({
                    serviceUrl,
                    publishableKey,
                    bookingNonce: sess.bookingNonce ?? undefined,
                });
            }
            catch (err) {
                if (gen !== sessionRequestGen)
                    return;
                console.error("startConversation failed:", err);
                set({ connectionState: "error", token: null, wsUrl: null });
            }
        },
        reset: () => {
            sessionRequestGen++;
            set({
                connectionState: "idle",
                agentDisplayState: "idle",
                lastAgentStateEvent: null,
                token: null,
                wsUrl: null,
                remoteAudioTrack: null,
                agentVoiceState: null,
                userMicVolume: 0,
                agentAudioLevel: 0,
                isMicMuted: false,
                micError: null,
                micRetryInFlight: false,
                // micRetryNonce intentionally not reset (monotonic; bridge fires only on change).
                latestPageContextPath: null,
                handoffStatus: "idle",
                handoffId: null,
                handoffOperatorName: null,
                transcript: [],
                streamingTranscripts: [],
                chatPrompts: [],
                sendChatMessage: null,
                pipelineMetrics: initialPipelineMetrics(),
                intakeOpen: false,
                intakeDone: false,
                answersDelivered: 0,
                sawUserTurnSinceAnswer: false,
                wasMicMutedBeforeIntake: false,
                bookingNonce: null,
                ...initialBookingSlice(),
            });
        },
    }));
}
